import React, { FC } from 'react';
import { Text, View } from 'react-native';
import i18n from 'i18n-js';
import { font as initialFont } from '@styles/fonts';
import { scale as initialScale } from '@components/frame';
import { formatToDateTime } from '@helpers';
import ItemTime from '@components/machines/components/detailsComponents/activeMachine/components/TimeLine';
import ModifyNCFileTypeButton from '@components/operatorPanel/components/elements/buttons/ModifyNCFileTypeButton';
import { HomePageCentralPartProps } from '@components/operatorPanel/components/HomePage/HomePageCentralPart';
import { useManageNCFileCentralPart } from './hooks/useManageNCFileCentralPart';

export interface NCFileCentralPartProps
  extends Pick<HomePageCentralPartProps, 'CC' | 'ncFileId' | 'machineStatus' | 'isOperator' | 'productionBatchId' | 'scale' | 'font'> {
  ncFileChange: boolean;
  ncFileSetup: boolean;
}

const NCFileCentralPart: FC<NCFileCentralPartProps> = ({
  CC,
  ncFileId,
  machineStatus,
  ncFileChange,
  ncFileSetup,
  isOperator,
  productionBatchId,
  scale = initialScale,
  font = initialFont,
}) => {
  const { ncFileData, offset, onModifyNCFileTypePress, subStatus, shouldDisplaySelectNCFileTypeButton, customStatusText } = useManageNCFileCentralPart({
    CC,
    ncFileId,
    machineStatus,
    ncFileChange,
    ncFileSetup,
    isOperator,
    productionBatchId,
    scale,
    font,
  });

  if (!ncFileData.wasLoaded || ncFileData.data?.ncFileId !== ncFileId) return null;

  const ncFileInfoStyles = [font['Subhead 1'], { color: CC('Label/Dark/Tertiary'), textAlign: 'center' }] as const;

  return (
    <View style={{ width: scale(510) }}>
      <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: scale(3), marginBottom: scale(28) }}>
        <Text style={[font['Body Large'], { color: CC('Label/Dark/Tertiary') }]}>{subStatus}</Text>
      </View>
      <Text style={[font['Headline Large'], { color: CC('Label/Dark/Primary'), textAlign: 'center', marginBottom: scale(7) }]}>
        {ncFileData.data?.ncFileName}
      </Text>

      <ItemTime
        machineStatus={machineStatus}
        timeOffset={offset}
        C={CC}
        selected={false}
        timelineFilledColor={CC('Label/Dark/AlwaysLight Primary')}
        timelineNotFilledColor={CC('Overlay/Dark/Dim light')}
        hideNCFile
        customStatusTextAlreadyTranslated
        customStatusText={customStatusText}
        withoutSlashBetweenRunningAndTargetTime
        separateTargetDuration
        timeFontType="Body Large"
        statusFontType="Body Large"
        timeColor={CC('Label/Dark/Tertiary')}
        statusColor={CC('Label/Dark/Tertiary')}
        currentTimeStyles={{
          position: 'absolute',
          left: 0,
        }}
        targetTimeStyles={{
          position: 'absolute',
          right: 0,
        }}
        containerStyles={{
          flexDirection: 'row',
          justifyContent: 'center',
        }}
        statusBlockStyles={{
          marginLeft: 0,
        }}
      />

      <View style={{ alignItems: 'center', gap: scale(4), marginTop: scale(24) }}>
        <Text style={[...ncFileInfoStyles]}>{i18n.t('First ran on')}</Text>
        <Text style={[...ncFileInfoStyles]}>{formatToDateTime({ t: ncFileData.data?.firstRanOn })}</Text>
        <Text style={{ textAlign: 'center' }}>
          <Text style={[...ncFileInfoStyles]}>{i18n.t('on')}</Text>{' '}
          <Text style={[...ncFileInfoStyles]}>{ncFileData.data?.firstRanByMachineName}</Text>
        </Text>
        <Text style={[...ncFileInfoStyles, { marginTop: scale(11) }]}>{i18n.t(`Classified as ${machineStatus.ncFileStatus} NC File`)}</Text>
        <Text style={[...ncFileInfoStyles]}>
          {i18n.t('on')} {ncFileData.data?.ncFileStatusSetOn ? formatToDateTime({ t: ncFileData.data.ncFileStatusSetOn }) : ''}
        </Text>
        <Text style={[...ncFileInfoStyles]}>
          {i18n.t('by')} {ncFileData.data?.ncFileStatusSetBy || ''}
        </Text>
      </View>
      {shouldDisplaySelectNCFileTypeButton && (
        <View style={{ marginTop: scale(20), alignItems: 'center' }}>
          <ModifyNCFileTypeButton onPress={() => onModifyNCFileTypePress()} CC={CC} scale={scale} font={font} />
        </View>
      )}
    </View>
  );
};

export default NCFileCentralPart;
